import { useEffect, useState } from "react";
import { useIsMobile } from "../lib/responsive.js";
import { useCanInstall, promptInstall, isStandalone, isIOS } from "../lib/pwa.js";
import { tourSeen } from "./WelcomeTour.js";

/**
 * A gentle bottom banner that helps people install the PWA to their home screen:
 *  - iOS Safari (which never fires an install prompt): shows the Share → Add to Home
 *    Screen steps with the Share glyph and an arrow toward the toolbar.
 *  - Android/Chrome (deferred beforeinstallprompt captured): an Install button that fires
 *    the native prompt.
 *
 * Appears a couple seconds after landing (once the first-run tour is out of the way), only
 * on mobile, only when not already installed. Dismissing snoozes it for a few days, so it
 * reminds again later without nagging.
 */
const SNOOZE_KEY = "gb.install.snoozed";
const COOLDOWN_MS = 5 * 24 * 60 * 60 * 1000; // remind again after ~5 days
const APPEAR_DELAY_MS = 2500;

function snoozed(): boolean {
  try {
    const t = Number(localStorage.getItem(SNOOZE_KEY));
    return Number.isFinite(t) && t > 0 && Date.now() - t < COOLDOWN_MS;
  } catch {
    return false;
  }
}

/** iOS Safari specifically — only it can "Add to Home Screen" (Chrome/FF on iOS can't). */
function isIOSSafari(): boolean {
  if (!isIOS()) return false;
  return !/CriOS|FxiOS|EdgiOS|OPiOS|GSA/.test(navigator.userAgent);
}

function ShareGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="15"
      height="15"
      className="mb-0.5 inline-block align-middle text-blue"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 3v12" />
      <path d="M8 7l4-4 4 4" />
      <path d="M6 12v6a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-6" />
    </svg>
  );
}

export function InstallBanner() {
  const isMobile = useIsMobile();
  const canInstall = useCanInstall();
  const [show, setShow] = useState(false);
  const [kind, setKind] = useState<"ios" | "android">("ios");

  useEffect(() => {
    if (!isMobile) return;
    if (isStandalone() || snoozed() || !tourSeen()) return;
    const eligible: "ios" | "android" | null = canInstall
      ? "android"
      : isIOSSafari()
        ? "ios"
        : null;
    if (!eligible) return;
    const t = setTimeout(() => {
      setKind(eligible);
      setShow(true);
    }, APPEAR_DELAY_MS);
    return () => clearTimeout(t);
  }, [isMobile, canInstall]);

  if (!show) return null;

  const dismiss = () => {
    setShow(false);
    try {
      localStorage.setItem(SNOOZE_KEY, String(Date.now()));
    } catch {
      /* private mode — just close for this session */
    }
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] animate-[gb-sheet-up_0.3s_cubic-bezier(0.32,0.72,0,1)] motion-reduce:animate-none">
      <div className="mx-auto flex max-w-md items-start gap-3 rounded-xl border border-border-2 bg-panel-2 p-3.5 shadow-2xl">
        <img src="/icon.svg" alt="" className="h-10 w-10 shrink-0 rounded-lg" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-ink">Install greenbook</div>
          {kind === "ios" ? (
            <p className="mt-0.5 text-[13px] leading-relaxed text-dim">
              Tap <ShareGlyph /> <span className="font-medium text-ink">Share</span> below, then{" "}
              <span className="font-medium text-ink">“Add to Home Screen.”</span> It opens
              full-screen with no browser bars.
            </p>
          ) : (
            <>
              <p className="mt-0.5 text-[13px] leading-relaxed text-dim">
                Add it to your home screen for a full-screen app — no browser address bar or
                toolbar.
              </p>
              <button
                onClick={async () => {
                  dismiss();
                  await promptInstall();
                }}
                className="mt-2 rounded-md bg-up/20 px-3 py-1.5 text-[13px] font-semibold text-up active:bg-up/30"
              >
                Install
              </button>
            </>
          )}
        </div>
        <button
          onClick={dismiss}
          aria-label="dismiss"
          className="-mr-1 -mt-1 grid h-8 w-8 shrink-0 place-items-center rounded-md text-lg text-dim hover:text-ink"
        >
          ✕
        </button>
      </div>
      {kind === "ios" && (
        <div className="mx-auto mt-0.5 w-fit text-sm text-dim" aria-hidden>
          ▾
        </div>
      )}
    </div>
  );
}
